import ssf from 'ssf';
import { utils as utilsCore } from '@industriousapps/excelkits-core';
import logger from '@/utils/logger';
import abbreviateNumber from './abbreviateNumber';
import { replaceNumbers } from './numbers';
import { trimTrailingDecimals } from './decimals';
import { getNumberFromString } from './spreadsheetFormat';
const PREFIX = 'common: utils: templateEngine:';
const {
  isValidNumber
} = utilsCore;
export const createSsfRegExp = () => / \| ssf.*/g;
export const createTrimDecRegExp = () => / \| trim_dec/g;
export const createAbbrRegExp = () => /\|\s*abbr\s*(k|m|b|auto)\s?(\d?)*/g; // Interpolate a template string with dynamic variables
// Based on: https://krasimirtsonev.com/blog/article/Javascript-template-engine-in-just-20-line

export default function templateEngine(template, context) {
  const abbr = createAbbrRegExp();
  const spreadsheetFmt = createSsfRegExp();
  const trimDec = createTrimDecRegExp();
  let result = `${template}`;
  let match;

  while (match = /{{([^}}]+)?}}/g.exec(result)) {
    const varName = match[1].replace(trimDec, '').replace(abbr, '').replace(spreadsheetFmt, '').trim();
    const varMap = templateVariableStringToMap(match[0]);
    let value = typeof context[varName] !== 'undefined' ? context[varName] : '';
    const src = match[0];
    const hasSSFDirective = src.search(spreadsheetFmt) > 0;
    const hasTrimDecDirective = src.search(trimDec) > 0;
    const hasAbbrDirective = src.search(abbr) > 0; // Apply spreadsheet formatting template

    if (hasSSFDirective) {
      let formatArg = getSSFArgument(src) || 'General';

      try {
        formatArg = decodeSFFArgument(formatArg); // Decode any URL space chars
      } catch (err) {
        logger.error(Error(`${PREFIX} failed to URL decode "${formatArg}": ${err}`));
      }

      const isNum = isValidNumber(value, false);
      value = ssf.format(formatArg, isNum ? Number(value) : value).trim();
    } // Apply decimal trimming if the flag is present
    // to remove any unnecessary decimal points


    if (hasTrimDecDirective) {
      value = trimTrailingDecimals(`${value}`);
    } // Apply abbreviation if the argument is present


    if (hasAbbrDirective) {
      const valueStr = `${value}`; // convert to string

      const abbrArgs = (varMap.get('abbr') || '').split(' ');
      const abbrSymbol = abbrArgs[0] || 'auto';
      const fixedDigit = abbrArgs[1] ? parseInt(abbrArgs[1], 10) : undefined;
      const abbrValue = abbreviateNumber(getNumberFromString(valueStr), {
        symbol: abbrSymbol === 'auto' ? '' : abbrSymbol,
        fixedDigit,
        omitNegativeSymbol: hasSSFDirective // Assume handled by SSF

      });
      value = replaceNumbers(valueStr, abbrValue);
    }

    if (typeof value === 'function') {
      value = '';
    } // Update tempate


    result = result.replace(src, `${value}`);
  }

  return result.replace(/<style[^>]*>.*<\/style>/gm, '') // Remove style tags
  .replace(/<script[^>]*>.*<\/script>/gm, ''); // Remove script tags
} // Wrap template variables
// in an underline HTML tag

export function underlineTemplateVariables(tmpl) {
  return tmpl.replace(/\n/gm, '').replace(/{{/gm, '<u>{{') // Add highlights under variables
  .replace(/\$<u>{{/gm, '<u>${{') // Move money symbol into highlight
  .replace(/}}/gm, '}}</u>') // Close highlight tags
  .replace(/}}<\/u>%/gm, '}}%</u>'); // Move percent symbol into highlight
} // Return the first variable
// name in a template

export function firstTemplateVariableName(tmpl) {
  return allTemplateVariableNames(tmpl).shift() || '';
} // Return the first variable
// name in a template

export function allTemplateVariableNames(tmpl) {
  const result = [];
  const regExp = /{{([^}}]+)?}}/g;
  let match;

  while (match = regExp.exec(tmpl)) {
    match.input = ''; // delete string copy
    // store the matching variable name /wo any helpers

    result.push((match[1] || '').split(' | ')[0]);
  }

  return result;
} // Lookup last user provided string
// or return a default value
// NOTE: supports new argument syntax: {{value | function arg1 arg2}}

export function getStringArgumentByIndex(src, index) {
  let defaultValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  const func = src.replace('}}', '').split(' | ').pop() || '';
  const args = func.split(' ').slice(1);
  const targetArg = args[index] || '';
  return targetArg ? targetArg : `${defaultValue}`;
} // Determine if a string contains
// uninterpolated values

export function isUninterpolated(str) {
  return /{{([^}}]+)?}}/g.test(str);
} // URI encode conflicting characters

const URI_SPACE = '%20';
export function encodeSSFArgument(arg) {
  return arg.replace(/\s/g, URI_SPACE);
} // URI decode conflicting characters

export function decodeSFFArgument(arg) {
  return arg.replace(/%20/g, ' ');
} // Lookup the SSF argument provided in a template string

export const getSSFArgument = template => {
  if (template.search(createSsfRegExp()) === -1) return ''; // Remove non ssf portions of template

  const ssfSegment = template.split('|').find(segment => segment.search('ssf') > -1) || 'ssf';
  const ssfOnlyTemplate = `{{value | ${ssfSegment.replace('}}', '').trim()}}}`;
  let formatArg = getStringArgumentByIndex(ssfOnlyTemplate, 0) || '';

  try {
    formatArg = decodeSFFArgument(formatArg); // Decode any URL space chars
  } catch {
    return '';
  }

  return formatArg || '';
}; // Check if template includes trim_dec

export function hasTrimDecDirective(template) {
  return template.search(createTrimDecRegExp()) > 0;
} // Convert a variable  within a template to a map
// ie `{{value | ssf ... | trim_dec}}` -> `{ssf: '...', trim_dec: ''}`

export function templateVariableStringToMap(tmplVar) {
  // Remove outer curly braces if they exist
  const cleanVar = tmplVar.replace(/^\{\{|\}\}$/g, ''); // Split by | and discard first item

  const [, ...args] = cleanVar.split('|').map(arg => arg.trim()); // Create map from remaining arguments

  return args.reduce((acc, arg) => {
    const [funcName, ...args] = arg.split(' ');
    acc.set(funcName, args.join(' '));
    return acc;
  }, new Map());
} // Convert a map to a template variable string
// ie `{ssf: '...', trim_dec: ''}` -> `{{value | ssf ... | trim_dec}}`

export function templateVariableMapToString(tmplVarMap) {
  let variableName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'value';
  const args = Array.from(tmplVarMap.entries()).filter(_ref => {
    let [key] = _ref;
    return Boolean(key);
  }).map(_ref2 => {
    let [key, value] = _ref2;
    return `${key} ${value}`.trim();
  });
  return `{{${variableName}${args.length ? ' | ' : ''}${args.join(' | ')}}}`;
} // Insert or update a directive into a
// template variable map at any desired index

export function upsertTemplateVariableMapAtIndex(tmplVarMap, key, value) {
  let idx = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : -1;

  if (tmplVarMap.has(key)) {
    const newTmplVarMap = new Map(tmplVarMap);
    newTmplVarMap.set(key, value);
    return newTmplVarMap;
  }

  const arr = Array.from(tmplVarMap); // Insert at last position, by default, otherwise
  // insert at the desired index, ensuring it's within bounds
  // of the array's length

  const targetIdx = idx < 0 ? arr.length : Math.min(idx, arr.length); // Insert the new directive at the target index

  arr.splice(targetIdx, 0, [key, value]); // Return a new, updated, map

  return new Map(arr);
}